import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import {
  LoginPage,
  OTPPage,
  SignalsListPage,
  CreateSignalPage,
  SignalDetailPage,
  EditSignalPage,
} from '@/pages';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--tg-theme-bg-color, #ffffff)',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '3px solid var(--tg-theme-button-color, #3390ec)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>
          {`@keyframes spin { to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/signals" replace />;
  }

  return <>{children}</>;
};

export const Router: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/otp"
          element={<OTPPage />}
        />

        {/* Protected Routes */}
        <Route
          path="/signals"
          element={
            <PrivateRoute>
              <SignalsListPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/signals/create"
          element={
            <PrivateRoute>
              <CreateSignalPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/signals/:id"
          element={
            <PrivateRoute>
              <SignalDetailPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/signals/:id/edit"
          element={
            <PrivateRoute>
              <EditSignalPage />
            </PrivateRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/signals" replace />} />
        <Route path="*" element={<Navigate to="/signals" replace />} />
      </Routes>
    </BrowserRouter>
  );
};
